// Curated PortraitMaster choices for the Human Portrait page. The public API
// accepts exactly these values (PORTRAIT_OPTIONS in
// server/src/routes/human-portrait.ts); keep the two in sync.
//
// Values are the EXACT strings the comfyui-portrait-master node expects (the
// worker forwards them verbatim onto the node inputs / prompt), so do not
// translate or reword them. The full upstream lists are far longer and noisy;
// these are sensible, deduplicated subsets.
//
// Each control is tri-state:
//   • Default  (value '')          → field omitted → node default → model decides,
//                                     no randomization.
//   • Randomize (value RANDOMIZE)  → resolveChoice() picks a random option from
//                                     the list and sends that concrete value.
//   • a specific value             → sent verbatim.

export type PortraitFieldKey =
  | 'shot'
  | 'gender'
  | 'age'
  | 'nationality_1'
  | 'nationality_2'
  | 'body_type'
  | 'breast_size'
  | 'butt_size'
  | 'face_shape'
  | 'jawline'
  | 'eyes_color'
  | 'eyes_shape'
  | 'nose_shape'
  | 'nose_size'
  | 'hair_color'
  | 'hair_style'
  | 'hair_length'
  | 'makeup'
  | 'makeup_color'
  | 'skin_tone'
  | 'skin_texture'
  | 'beard';

export type PortraitField = {
  key: PortraitFieldKey;
  label: string;
  options: string[];
  // 'autocomplete' for long lists (nationality); 'select' otherwise.
  variant: 'select' | 'autocomplete';
  // Visual grouping on the page.
  group: 'framing' | 'identity' | 'body' | 'face' | 'hair' | 'makeup';
  // beard only makes sense for masculine subjects; shown but optional.
  hint?: string;
};

export const GENDERS = ['Woman', 'Man'] as const;

// Camera framing / shot length (PortraitMaster `shot`). Ordered tight → wide.
// "Half-length portrait" is the waist-up 半身照. Default selection on the page is
// half-length so faces stay detailed instead of drifting to full-body.
export const SHOTS = [
  'Head portrait',
  'Head and shoulders portrait',
  'Half-length portrait',
  'Portrait',
  'Full-length portrait',
  'Full body',
  'Close-up',
];
export const DEFAULT_SHOT = 'Half-length portrait';

// How strongly the chosen framing is enforced (PortraitMaster `shot_weight`).
// Higher = the shot tag wins harder against full-body drift. Only applies when a
// framing/shot is selected.
export const SHOT_WEIGHTS: { value: number; label: string }[] = [
  { value: 1.0, label: 'Subtle' },
  { value: 1.3, label: 'Default' },
  { value: 1.5, label: 'Strong' },
  { value: 1.8, label: 'Very strong' },
];
export const DEFAULT_SHOT_WEIGHT = 1.3;

export const AGES = ['18', '20', '24', '28', '32', '36', '40', '44', '50', '56', '62', '70', '80'];

// Full nationality list (sorted) — used for the two nationality autocompletes.
export const NATIONALITIES = [
  'Afghan', 'Albanian', 'Algerian', 'American', 'Andorran', 'Angolan', 'Argentine', 'Armenian',
  'Australian', 'Austrian', 'Azerbaijani', 'Bahamian', 'Bahraini', 'Bangladeshi', 'Barbadian',
  'Belarusian', 'Belgian', 'Belizean', 'Beninese', 'Bhutanese', 'Bolivian', 'Bosnian Herzegovinian',
  'Brazilian', 'British', 'Bruneian', 'Bulgarian', 'Burkinabe', 'Burmese', 'Burundian', 'Cambodian',
  'Cameroonian', 'Canadian', 'Cape Verdian', 'Central African', 'Chadian', 'Chilean', 'Chinese',
  'Colombian', 'Comoran', 'Congolese', 'Costa Rican', 'Croatian', 'Cuban', 'Cypriot', 'Czech',
  'Danish', 'Djibouti', 'Dominican', 'Dutch', 'East Timorese', 'Ecuadorean', 'Egyptian', 'Emirian',
  'Equatorial Guinean', 'Eritrean', 'Estonian', 'Ethiopian', 'Fijian', 'Filipino', 'Finnish',
  'French', 'Gabonese', 'Gambian', 'Georgian', 'German', 'Ghanaian', 'Greek', 'Grenadian',
  'Guatemalan', 'Guinean', 'Guyanese', 'Haitian', 'Honduran', 'Hungarian', 'Icelander', 'Indian',
  'Indonesian', 'Iranian', 'Iraqi', 'Irish', 'Israeli', 'Italian', 'Ivorian', 'Jamaican', 'Japanese',
  'Jordanian', 'Kazakhstani', 'Kenyan', 'Korean', 'Kuwaiti', 'Kyrgyz', 'Laotian', 'Latvian',
  'Lebanese', 'Liberian', 'Libyan', 'Lithuanian', 'Luxembourgish', 'Macedonian', 'Malagasy',
  'Malawian', 'Malaysian', 'Maldivan', 'Malian', 'Maltese', 'Mauritanian', 'Mauritian', 'Mexican',
  'Moldovan', 'Mongolian', 'Montenegrin', 'Moroccan', 'Mozambican', 'Namibian', 'Nepalese',
  'New Zealander', 'Nicaraguan', 'Nigerian', 'North Korean', 'Norwegian', 'Omani', 'Pakistani',
  'Palestinian', 'Panamanian', 'Paraguayan', 'Peruvian', 'Polish', 'Portuguese', 'Qatari',
  'Romanian', 'Russian', 'Rwandan', 'Salvadoran', 'Samoan', 'Saudi', 'Scottish', 'Senegalese',
  'Serbian', 'Singaporean', 'Slovakian', 'Slovenian', 'Somali', 'South African', 'South Korean',
  'Spanish', 'Sri Lankan', 'Sudanese', 'Swazi', 'Swedish', 'Swiss', 'Syrian', 'Tajikistani',
  'Tanzanian', 'Thai', 'Togolese', 'Tongan', 'Tunisian', 'Turkish', 'Turkmen', 'Ugandan',
  'Ukrainian', 'Uruguayan', 'Uzbekistani', 'Venezuelan', 'Vietnamese', 'Welsh', 'Yemeni', 'Zambian',
  'Zimbabwean',
];

const BODY_TYPES = ['Slim', 'Slight', 'Petite', 'Fit', 'Curvy', 'Voluptuous', 'Thick', 'Muscular', 'Buxom', 'Chubby', 'Plump', 'Stocky', 'Tall', 'Short'];
const BREAST_SIZES = ['flat', 'small', 'medium', 'large', 'huge'];
const BUTT_SIZES = ['small', 'medium', 'large', 'athletic'];

const FACE_SHAPES = ['Oval', 'Round', 'Square', 'Heart', 'Diamond', 'Oblong', 'Long', 'Triangle', 'Inverted Triangle', 'Pear', 'Rectangle'];
const JAWLINES = ['defined', 'soft', 'rounded', 'square', 'angular', 'sharp', 'delicate', 'strong', 'wide', 'narrow'];
const EYES_COLORS = ['Amber', 'Blue', 'Brown', 'Green', 'Gray', 'Hazel', 'Violet', 'Heterochromia'];
const EYES_SHAPES = ['Almond Eyes Shape', 'Asian Eyes Shape', 'Close-Set Eyes Shape', 'Deep Set Eyes Shape', 'Downturned Eyes Shape', 'Double Eyelid Eyes Shape', 'Hooded Eyes Shape', 'Monolid Eyes Shape', 'Oval Eyes Shape', 'Round Eyes Shape', 'Upturned Eyes Shape'];
const NOSE_SHAPES = ['straight', 'aquiline', 'roman', 'button', 'upturned', 'broad', 'narrow', 'small', 'large', 'sharp', 'rounded', 'angular', 'delicate', 'defined'];
const NOSE_SIZES = ['small', 'medium', 'large', 'wide', 'narrow', 'prominent', 'subtle', 'delicate'];

const HAIR_COLORS = ['Auburn', 'Black', 'Jet Black', 'Blonde', 'Strawberry', 'Chestnut', 'Chocolate', 'Caramel', 'Copper', 'Gray', 'Honey', 'Mahogany', 'Platinum', 'Red', 'Silver', 'White', 'Burgundy', 'Multicolored', 'Pastel'];
const HAIR_STYLES = ['Afro', 'Bald', 'Bangs', 'Bob', 'Blunt bob', 'Braided', 'Box braids', 'Bun', 'Top Knot', 'Buzz', 'Cornrows', 'Curly', 'Curly bob', 'Dreadlocks', 'Faux hawk', 'Layered', 'Long straight', 'Long ponytail', 'High ponytail', 'Mohawk', 'Pixie', 'Pompadour', 'Quiff', 'Shag', 'Short', 'Short curly', 'Side braid', 'Spiky', 'Undercut', 'Updo', 'Very long wave', 'Wavy', 'Wavy bob'];
const HAIR_LENGTHS = ['Short', 'Medium', 'Long'];

const MAKEUPS = ['Natural Makeup', 'Glam Makeup', 'Glowy Makeup', 'Dewy Makeup', 'Classic Makeup', 'Cut Crease Makeup', 'Edgy Makeup', 'Festival Makeup', 'Golden Makeup', 'Monochromatic Makeup', 'Party Makeup', 'Retro Makeup', 'Smokey Eye Makeup', 'Sunset Eye Makeup', 'Vintage Makeup', 'Watercolor Makeup', 'Anime Makeup', 'Artistic Makeup', 'Avant-garde Makeup', 'Bohemian Makeup'];
const MAKEUP_COLORS = ['Red', 'Pink', 'Coral', 'Peach', 'Mauve', 'Burgundy', 'Plum', 'Purple', 'Brown', 'Copper', 'Gold', 'Silver', 'Black', 'Wine', 'Raspberry', 'Orange', 'Nude', 'Berry'];
const SKIN_TONES = ['fair', 'medium', 'dark', 'olive', 'tan', 'bronzed', 'golden', 'porcelain', 'alabaster', 'ebony', 'chocolate', 'caramel', 'honey', 'peach', 'ivory', 'rose', 'sand'];
const SKIN_TEXTURES = ['smooth', 'normal', 'dry', 'oily', 'combination', 'poreless', 'fine-textured', 'freckled', 'mole-covered', 'wrinkled', 'mature', 'youthful', 'dewy', 'matte', 'glowing', 'radiant'];
const BEARDS = ['Stubble Beard', 'Goatee', 'Full Beard', 'Van Dyke Beard', 'Soul Patch', 'Garibaldi Beard', 'Circle Beard', 'Corporate Beard', 'Balbo Beard', 'Ducktail Beard', 'Chinstrap Beard', 'Anchor Beard', 'Chevron Mustache', 'Horseshoe Mustache', 'Handlebar Mustache', 'Imperial Mustache', 'Pencil Mustache', 'Zappa Mustache'];

// Field render order. gender / age live in their own top controls, so they are
// not in this list. Everything here renders as a labelled Random-or-pick input.
export const PORTRAIT_FIELDS: PortraitField[] = [
  { key: 'shot', label: 'Framing', options: SHOTS, variant: 'select', group: 'framing', hint: 'Half-length = waist-up portrait' },

  { key: 'nationality_1', label: 'Nationality', options: NATIONALITIES, variant: 'autocomplete', group: 'identity' },
  { key: 'nationality_2', label: 'Nationality (mix)', options: NATIONALITIES, variant: 'autocomplete', group: 'identity', hint: 'Optional second nationality to blend' },

  { key: 'body_type', label: 'Body type', options: BODY_TYPES, variant: 'select', group: 'body' },
  { key: 'breast_size', label: 'Breast size', options: BREAST_SIZES, variant: 'select', group: 'body' },
  { key: 'butt_size', label: 'Butt size', options: BUTT_SIZES, variant: 'select', group: 'body' },
  { key: 'skin_tone', label: 'Skin tone', options: SKIN_TONES, variant: 'select', group: 'body' },
  { key: 'skin_texture', label: 'Skin texture', options: SKIN_TEXTURES, variant: 'select', group: 'body' },

  { key: 'face_shape', label: 'Face shape', options: FACE_SHAPES, variant: 'select', group: 'face' },
  { key: 'jawline', label: 'Jawline', options: JAWLINES, variant: 'select', group: 'face' },
  { key: 'eyes_color', label: 'Eye color', options: EYES_COLORS, variant: 'select', group: 'face' },
  { key: 'eyes_shape', label: 'Eye shape', options: EYES_SHAPES, variant: 'select', group: 'face' },
  { key: 'nose_shape', label: 'Nose shape', options: NOSE_SHAPES, variant: 'select', group: 'face' },
  { key: 'nose_size', label: 'Nose size', options: NOSE_SIZES, variant: 'select', group: 'face' },

  { key: 'hair_color', label: 'Hair color', options: HAIR_COLORS, variant: 'select', group: 'hair' },
  { key: 'hair_style', label: 'Hair style', options: HAIR_STYLES, variant: 'select', group: 'hair' },
  { key: 'hair_length', label: 'Hair length', options: HAIR_LENGTHS, variant: 'select', group: 'hair' },
  { key: 'beard', label: 'Beard / facial hair', options: BEARDS, variant: 'select', group: 'hair', hint: 'Applies to masculine subjects' },

  { key: 'makeup', label: 'Makeup', options: MAKEUPS, variant: 'select', group: 'makeup' },
  { key: 'makeup_color', label: 'Makeup color', options: MAKEUP_COLORS, variant: 'select', group: 'makeup' },
];

export const PORTRAIT_GROUPS: { key: PortraitField['group']; label: string }[] = [
  { key: 'framing', label: 'Framing & shot' },
  { key: 'identity', label: 'Identity' },
  { key: 'body', label: 'Body & skin' },
  { key: 'face', label: 'Face & eyes' },
  { key: 'hair', label: 'Hair & facial hair' },
  { key: 'makeup', label: 'Makeup' },
];

// Sentinel for the tri-state "Randomize" choice. Distinct enough that it can't
// collide with a real PortraitMaster value.
export const RANDOMIZE = '__randomize__';

// Resolve a tri-state selection to the concrete string to send, or undefined to
// omit the field (Default → model decides). RANDOMIZE picks a random option.
export function resolveChoice(value: string | undefined, options: readonly string[]): string | undefined {
  if (!value) return undefined;
  if (value === RANDOMIZE) {
    if (options.length === 0) return undefined;
    return options[Math.floor(Math.random() * options.length)];
  }
  return value;
}

// ─── Localization ──────────────────────────────────────────────────────────
// Chinese DISPLAY labels only. The value sent to the worker is always the exact
// English PortraitMaster enum (the node validates against its English lists), so
// we never translate the value — only what the user sees. Keys are either a bare
// English value, or `fieldKey:value` for the handful of values whose meaning
// differs by field (e.g. "Short" as a body type vs hair length). Field/group
// labels and the UI words Default/Randomize are keyed by their English text.
// Untranslated entries fall back to the English string.
const PORTRAIT_ZH: Record<string, string> = {
  // UI
  'Default': '默认', 'Randomize': '随机', '🎲 Randomize': '🎲 随机',
  // Field labels
  'Framing': '取景', 'Nationality': '国籍', 'Nationality (mix)': '国籍（混血）',
  // Framing group + shot values
  'Framing & shot': '取景与景别',
  'Half-length = waist-up portrait': '半身照 = 腰部以上',
  'Head portrait': '头部特写', 'Head and shoulders portrait': '头肩照',
  'Half-length portrait': '半身照', 'Portrait': '肖像',
  'Full-length portrait': '全身照', 'Full body': '全身', 'Close-up': '大特写',
  // Framing strength
  'Framing strength': '取景强度',
  'Subtle': '轻微', 'Strong': '强', 'Very strong': '很强',
  'Body type': '体型', 'Breast size': '胸围', 'Butt size': '臀围',
  'Skin tone': '肤色', 'Skin texture': '肤质',
  'Face shape': '脸型', 'Jawline': '下颌线', 'Eye color': '眼睛颜色',
  'Eye shape': '眼型', 'Nose shape': '鼻型', 'Nose size': '鼻子大小',
  'Hair color': '发色', 'Hair style': '发型', 'Hair length': '发长',
  'Beard / facial hair': '胡须 / 面部毛发', 'Makeup': '妆容', 'Makeup color': '妆容颜色',
  // Hints
  'Optional second nationality to blend': '可选的第二国籍，用于混血',
  'Applies to masculine subjects': '适用于男性形象',
  // Group labels
  'Identity': '身份', 'Body & skin': '身材与皮肤', 'Face & eyes': '面部与眼睛',
  'Hair & facial hair': '头发与胡须',
  // Gender
  'Woman': '女性', 'Man': '男性',
  // Body type
  'body_type:Short': '矮小',
  'Slim': '苗条', 'Slight': '纤瘦', 'Petite': '娇小', 'Fit': '健美',
  'Curvy': '曲线玲珑', 'Voluptuous': '丰腴', 'Thick': '壮实', 'Muscular': '肌肉型',
  'Buxom': '胸部丰满', 'Chubby': '微胖', 'Plump': '丰满', 'Stocky': '敦实', 'Tall': '高挑',
  // Sizes (shared by breast/butt/nose)
  'flat': '平坦', 'small': '小', 'medium': '中等', 'large': '大', 'huge': '巨大',
  'athletic': '健美',
  // Skin tone
  'fair': '白皙', 'dark': '深色', 'olive': '橄榄色', 'tan': '小麦色', 'bronzed': '古铜色',
  'golden': '金棕色', 'porcelain': '瓷白', 'alabaster': '雪白', 'ebony': '乌黑',
  'chocolate': '巧克力色', 'caramel': '焦糖色', 'honey': '蜂蜜色', 'peach': '桃色',
  'ivory': '象牙白', 'rose': '玫瑰色', 'sand': '沙色',
  // Skin texture
  'smooth': '光滑', 'normal': '正常', 'dry': '干性', 'oily': '油性', 'combination': '混合性',
  'poreless': '无毛孔', 'fine-textured': '细腻', 'freckled': '雀斑', 'mole-covered': '有痣',
  'wrinkled': '有皱纹', 'mature': '成熟', 'youthful': '年轻', 'dewy': '水润', 'matte': '哑光',
  'glowing': '透亮', 'radiant': '光彩照人',
  // Face shape
  'Oval': '椭圆形', 'Round': '圆形', 'Square': '方形', 'Heart': '心形', 'Diamond': '菱形',
  'Oblong': '长方形', 'face_shape:Long': '长形', 'Triangle': '三角形',
  'Inverted Triangle': '倒三角形', 'Pear': '梨形', 'Rectangle': '矩形',
  // Jawline / nose descriptors (shared)
  'defined': '轮廓分明', 'soft': '柔和', 'rounded': '圆润', 'square': '方正',
  'angular': '棱角分明', 'sharp': '锐利', 'delicate': '精致', 'strong': '硬朗',
  'wide': '宽', 'narrow': '窄', 'prominent': '突出', 'subtle': '含蓄',
  'straight': '笔直', 'aquiline': '鹰钩', 'roman': '罗马式', 'button': '小巧', 'upturned': '上翘',
  'broad': '宽阔',
  // Eye color
  'Amber': '琥珀色', 'Blue': '蓝色', 'Brown': '棕色', 'Green': '绿色', 'Gray': '灰色',
  'Hazel': '淡褐色', 'Violet': '紫罗兰色', 'Heterochromia': '异色瞳',
  // Eye shape
  'Almond Eyes Shape': '杏仁眼', 'Asian Eyes Shape': '亚洲眼型', 'Close-Set Eyes Shape': '近距眼',
  'Deep Set Eyes Shape': '深陷眼', 'Downturned Eyes Shape': '下垂眼', 'Double Eyelid Eyes Shape': '双眼皮',
  'Hooded Eyes Shape': '内双', 'Monolid Eyes Shape': '单眼皮', 'Oval Eyes Shape': '椭圆眼',
  'Round Eyes Shape': '圆眼', 'Upturned Eyes Shape': '上扬眼',
  // Hair color
  'Auburn': '赤褐色', 'Black': '黑色', 'Jet Black': '乌黑', 'Blonde': '金色',
  'Strawberry': '草莓金', 'Chestnut': '栗色', 'Chocolate': '巧克力棕', 'Copper': '铜色',
  'Mahogany': '红木色', 'Platinum': '铂金色', 'Red': '红色', 'Silver': '银色', 'White': '白色',
  'Burgundy': '酒红色', 'Multicolored': '多彩', 'Pastel': '柔彩色',
  // Hair style
  'Afro': '爆炸头', 'Bald': '光头', 'Bangs': '刘海', 'Bob': '波波头', 'Blunt bob': '齐切波波头',
  'Braided': '编发', 'Box braids': '方块脏辫', 'Bun': '发髻', 'Top Knot': '顶髻', 'Buzz': '寸头',
  'Cornrows': '玉米辫', 'Curly': '卷发', 'Curly bob': '卷波波头', 'Dreadlocks': '脏辫',
  'Faux hawk': '仿莫西干', 'Layered': '层次发', 'Long straight': '长直发', 'Long ponytail': '长马尾',
  'High ponytail': '高马尾', 'Mohawk': '莫西干', 'Pixie': '精灵短发', 'Pompadour': '蓬帕杜',
  'Quiff': '飞机头', 'Shag': '碎发', 'hair_style:Short': '短发', 'Short curly': '短卷发',
  'Side braid': '侧编发', 'Spiky': '刺猬头', 'Undercut': '底切', 'Updo': '盘发',
  'Very long wave': '超长波浪', 'Wavy': '波浪卷', 'Wavy bob': '波浪波波头',
  // Hair length
  'hair_length:Short': '短', 'hair_length:Medium': '中长', 'hair_length:Long': '长',
  // Makeup
  'Natural Makeup': '自然妆', 'Glam Makeup': '魅惑妆', 'Glowy Makeup': '光泽妆', 'Dewy Makeup': '水光妆',
  'Classic Makeup': '经典妆', 'Cut Crease Makeup': '切割眼妆', 'Edgy Makeup': '前卫妆',
  'Festival Makeup': '节日妆', 'Golden Makeup': '金色妆', 'Monochromatic Makeup': '单色妆',
  'Party Makeup': '派对妆', 'Retro Makeup': '复古妆', 'Smokey Eye Makeup': '烟熏妆',
  'Sunset Eye Makeup': '落日眼妆', 'Vintage Makeup': '怀旧妆', 'Watercolor Makeup': '水彩妆',
  'Anime Makeup': '动漫妆', 'Artistic Makeup': '艺术妆', 'Avant-garde Makeup': '先锋妆',
  'Bohemian Makeup': '波西米亚妆',
  // Makeup color
  'Pink': '粉色', 'Coral': '珊瑚色', 'Mauve': '芋紫色', 'Plum': '梅子色', 'Purple': '紫色',
  'Gold': '金色', 'Wine': '酒红', 'Raspberry': '覆盆子色', 'Orange': '橙色', 'Nude': '裸色',
  'Berry': '莓果色',
  // Beard
  'Stubble Beard': '胡茬', 'Goatee': '山羊胡', 'Full Beard': '络腮胡', 'Van Dyke Beard': '凡戴克胡',
  'Soul Patch': '下唇须', 'Garibaldi Beard': '加里波第胡', 'Circle Beard': '环形胡',
  'Corporate Beard': '商务胡', 'Balbo Beard': '巴尔博胡', 'Ducktail Beard': '鸭尾胡',
  'Chinstrap Beard': '下巴带胡', 'Anchor Beard': '锚形胡', 'Chevron Mustache': '人字胡',
  'Horseshoe Mustache': '马蹄胡', 'Handlebar Mustache': '翘角胡', 'Imperial Mustache': '帝王胡',
  'Pencil Mustache': '铅笔胡', 'Zappa Mustache': '扎帕胡',
  // Common nationalities (long tail falls back to English)
  'American': '美国', 'Australian': '澳大利亚', 'Brazilian': '巴西', 'British': '英国',
  'Canadian': '加拿大', 'Chinese': '中国', 'Dutch': '荷兰', 'Egyptian': '埃及', 'Filipino': '菲律宾',
  'Finnish': '芬兰', 'French': '法国', 'German': '德国', 'Greek': '希腊', 'Indian': '印度',
  'Indonesian': '印度尼西亚', 'Iranian': '伊朗', 'Irish': '爱尔兰', 'Israeli': '以色列',
  'Italian': '意大利', 'Japanese': '日本', 'Korean': '韩国', 'Lebanese': '黎巴嫩', 'Malaysian': '马来西亚',
  'Mexican': '墨西哥', 'Moroccan': '摩洛哥', 'Nigerian': '尼日利亚', 'Norwegian': '挪威',
  'Pakistani': '巴基斯坦', 'Polish': '波兰', 'Portuguese': '葡萄牙', 'Russian': '俄罗斯', 'Saudi': '沙特',
  'Singaporean': '新加坡', 'South African': '南非', 'South Korean': '韩国', 'Spanish': '西班牙',
  'Swedish': '瑞典', 'Swiss': '瑞士', 'Thai': '泰国', 'Turkish': '土耳其', 'Ukrainian': '乌克兰',
  'Vietnamese': '越南',
};

const ZH_LOCALE = 'zh-CN';

// Translate a display string for the given locale. Pass `fieldKey` to disambiguate
// values whose meaning depends on the field (e.g. "Short"). Always returns the
// English string for non-zh locales / untranslated entries — the canonical value
// sent to the worker is never affected by this.
export function localize(locale: string, text: string, fieldKey?: string): string {
  if (locale !== ZH_LOCALE) return text;
  if (fieldKey && PORTRAIT_ZH[`${fieldKey}:${text}`]) return PORTRAIT_ZH[`${fieldKey}:${text}`];
  return PORTRAIT_ZH[text] ?? text;
}
